// ADR-073: panelist-facing reward values are TT$ airtime. The integer point
// ledger and 1 point = 1 US cent internal cost reference stay unchanged.
export const POINTS_PER_TTD = 22 as const;
export const MIN_CASHOUT_TTD_CENTS = 2_500 as const;
export const MIN_CASHOUT_POINTS = 550 as const;

const ttdFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export function pointsToTtdCentsFloor(points: number): number {
  if (!Number.isFinite(points)) {
    throw new Error('points_must_be_finite');
  }

  return Math.floor((points * 100) / POINTS_PER_TTD);
}

export function ttdCentsToPointsCeil(ttdCents: number): number {
  if (!Number.isFinite(ttdCents)) {
    throw new Error('ttd_cents_must_be_finite');
  }

  return Math.ceil((ttdCents * POINTS_PER_TTD) / 100);
}

export function formatTtdFromPoints(points: number): string {
  return `TT$${ttdFormatter.format(pointsToTtdCentsFloor(points) / 100)}`;
}

// ADR-058/073: source-of-truth boundary convention lives in
// Methodology/Incentive Model.md line 55: exactly 10 minutes books into
// the 5-10 bracket, exactly 11 minutes books into the 10-20 bracket.

export type LoiBracket = {
  displayLabel: string;
  maxMinutes: number;
  minMinutes: number;
  points: number;
  ttdCents: number;
  usdCents: number;
};

// ADR-073
export const LOI_BRACKETS = [
  {
    displayLabel: '5-10 min',
    maxMinutes: 10,
    minMinutes: 5,
    points: 110,
    ttdCents: 500,
    usdCents: 110,
  },
  {
    displayLabel: '10-20 min',
    maxMinutes: 20,
    minMinutes: 11,
    points: 220,
    ttdCents: 1_000,
    usdCents: 220,
  },
  {
    displayLabel: '20-30 min',
    maxMinutes: 30,
    minMinutes: 21,
    points: 330,
    ttdCents: 1_500,
    usdCents: 330,
  },
  {
    displayLabel: '30-45 min',
    maxMinutes: 45,
    minMinutes: 31,
    points: 440,
    ttdCents: 2_000,
    usdCents: 440,
  },
] as const satisfies readonly LoiBracket[];

export const DISQUALIFICATION_CREDIT_USD_CENTS = 5 as const; // ADR-058
export const PROFILE_WAVE_1_USD_CENTS = 0 as const; // ADR-058, amended 2026-05-29

export function resolveLoiBracket(loiMinutes: number | null | undefined): LoiBracket | null {
  if (typeof loiMinutes !== 'number' || !Number.isFinite(loiMinutes)) {
    return null;
  }

  const floored = Math.floor(loiMinutes);
  if (floored < 5 || floored > 45) {
    return null;
  }

  return (
    LOI_BRACKETS.find(
      (bracket) => floored >= bracket.minMinutes && floored <= bracket.maxMinutes,
    ) ?? null
  );
}
